import { Collection } from 'firestorter';

import CollectionName from '../utils/collections';
import { FileType, getFileType } from '../utils/files';
import BaseDocument from './BaseDocument';

const DOCUMENTS_PAGE_SIZE = 30;

const ClientDocumentSource = {
  CHAT: 'CHAT',
  UPLOAD: 'UPLOAD',
};

/**
 * Represents a file attached to a client's profile in the clientDocument Firestore collection.
 *
 * Documents are created in two ways:
 *   - Automatically when a non-audio file is sent in a chat message (source: CHAT, isSaved: false).
 *   - Manually when a coach uploads a file via the Saved Documents panel (source: UPLOAD, isSaved: true).
 */
class ClientDocument extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CLIENT_DOCUMENT}/${id}`, opts);
  }

  get assetPath() {
    return this.data.assetPath || '';
  }

  get fileName() {
    return this.data.fileName || '';
  }

  get fileSize() {
    return this.data.fileSize || 0;
  }

  get mimeType() {
    return this.data.mimeType || '';
  }

  get type() {
    return this.data.type || '';
  }

  get channelId() {
    return this.data.channelId || '';
  }

  get messageId() {
    return this.data.messageId || '';
  }

  get uploadedBy() {
    return this.data.uploadedBy || '';
  }

  get uploadedByName() {
    return this.data.uploadedByName || '';
  }

  get uploadedAt() {
    return this.data.uploadedAt?.toDate() || null;
  }

  get savedAt() {
    return this.data.savedAt?.toDate() || null;
  }

  get savedBy() {
    return this.data.savedBy || '';
  }

  get coachId() {
    return this.data.coachId;
  }

  get userId() {
    return this.data.userId;
  }

  get isSaved() {
    return !!this.data.isSaved;
  }

  get isRemoved() {
    return !!this.data.isRemoved;
  }

  /** How the document was created — CHAT (from a message) or UPLOAD (direct upload). */
  get source() {
    return this.data.source || ClientDocumentSource.CHAT;
  }

  /** Renames the document. Does not affect the underlying file in Storage. */
  rename(newName) {
    return this.updateFields({ fileName: newName });
  }

  /** Soft-deletes the document. Excluded from all queries after this call. */
  remove() {
    return this.updateFields({ isRemoved: true });
  }

  /**
   * Toggles the document's saved/pinned state.
   * savedAt and savedBy are only stamped when pinning — not when unpinning.
   */
  toggleSaved(savedBy) {
    const saving = !this.isSaved;
    return this.updateFields({
      isSaved: saving,
      ...(saving && { savedAt: new Date(), savedBy }),
    });
  }

  /**
   * Returns a paginated firestorter Collection of documents for a client.
   * Uses a one-shot fetch — no real-time listener is created.
   * Pass `coachId` to see one coach's files; omit it to see every coach's.
   */
  static getDocuments({
    userId,
    channelId,
    onlySaved = false,
    coachId = null,
    startAfterDoc = null,
  } = {}) {
    return new Collection(CollectionName.CLIENT_DOCUMENT, {
      createDocument: (src, opts) => new ClientDocument(src.id, opts),
      query: (ref) => {
        let query = ref
          .where('userId', '==', userId)
          .where('isRemoved', '==', false);

        if (onlySaved) query = query.where('isSaved', '==', true);
        if (channelId) query = query.where('channelId', '==', channelId);
        if (coachId) query = query.where('coachId', '==', coachId);

        query = query.orderBy('uploadedAt', 'desc').limit(DOCUMENTS_PAGE_SIZE);

        if (startAfterDoc) query = query.startAfter(startAfterDoc.snapshot);

        return query;
      },
    });
  }

  /**
   * Soft-deletes all non-removed documents associated with a chat message.
   * Safe to call for any message — only documents created for non-audio attachments
   * will exist, so no over-deletion can occur.
   */
  static async removeByMessageId(messageId) {
    const collection = new Collection(CollectionName.CLIENT_DOCUMENT, {
      createDocument: (src, opts) => new ClientDocument(src.id, opts),
      query: (ref) => ref.where('messageId', '==', messageId).where('isRemoved', '==', false),
    });
    await collection.fetch();
    await Promise.all(collection.docs.map((doc) => doc.updateFields({ isRemoved: true })));
  }

  /**
   * Creates a new client document record for a directly-uploaded file.
   * The document is immediately pinned (isSaved: true, source: UPLOAD).
   * @returns {Promise<string>} The Firestore document ID of the created record.
   */
  static async addDoc({
    assetPath,
    fileName,
    fileSize,
    mimeType,
    uploadedBy,
    uploadedByName,
    coachId,
    userId,
  }) {
    const collection = new Collection(CollectionName.CLIENT_DOCUMENT);
    const { id } = await collection.add({
      assetPath,
      fileName,
      fileSize,
      mimeType,
      type: mimeType === 'application/pdf' ? FileType.PDF : getFileType(mimeType),
      uploadedBy,
      uploadedByName,
      uploadedAt: new Date(),
      savedAt: new Date(),
      savedBy: uploadedBy,
      coachId,
      userId,
      source: ClientDocumentSource.UPLOAD,
      isSaved: true,
      isRemoved: false,
    });
    return id;
  }
}

export default ClientDocument;
export { DOCUMENTS_PAGE_SIZE, ClientDocumentSource };
